import { type CSSProperties, type RefObject, useEffect, useRef, useState } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconChevronDown, IconChevronLeft, IconChevronRight, IconChevronUp } from '@tabler/icons-react';
import {
    CarouselLayout,
    GridLayout,
    isTrackReference,
    type TrackReference,
    type TrackReferenceOrPlaceholder,
    useGridLayout,
    useLayoutContext,
    usePinnedTracks,
    useTracks,
} from '@livekit/components-react';
import { RoomEvent, Track } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import { useCallLayout } from './call-layout';
import classes from './call-stage.module.css';
import { FloatingTile } from './floating-tile';
import { ParticipantTile } from './participant-tile';
import { ResizableSplit } from './resizable-split';
import { orderScreenShares } from './screen-share-order';
import { useSpeakerTrack } from './speaker-track';
import { isSameTrackRef } from './track-refs';

function isScreenShareTrack(track: TrackReferenceOrPlaceholder): track is TrackReference {
    return isTrackReference(track) && track.source === Track.Source.ScreenShare;
}

function StageGrid({ tracks }: Readonly<{ tracks: TrackReferenceOrPlaceholder[] }>) {
    const frameRef = useRef<HTMLDivElement>(null);
    const { layout: grid } = useGridLayout(frameRef as RefObject<HTMLDivElement>, tracks.length);
    const lastRowTiles = tracks.length <= grid.maxTiles ? tracks.length % grid.columns : 0;
    const spans = {
        '--tile-span': lastRowTiles || 1,
        '--grid-subcolumns': grid.columns * (lastRowTiles || 1),
        '--last-row-span': grid.columns,
    } as CSSProperties;

    return (
        <div ref={frameRef} className={classes.gridFrame}>
            <GridLayout className={classes.grid} data-last-row={lastRowTiles} style={spans} tracks={tracks}>
                <ParticipantTile />
            </GridLayout>
        </div>
    );
}

export function CallStage() {
    const { t } = useTranslation('conference');
    const layoutContext = useLayoutContext();
    const { layout } = useCallLayout();
    const [filmstripVisible, setFilmstripVisible] = useState(true);
    const screenShareOrderRef = useRef<string[]>([]);
    const previousLayoutRef = useRef(layout);
    const stacked = useMediaQuery('(orientation: portrait)', false, { getInitialValueInEffect: false });

    const tracks = useTracks(
        [
            { source: Track.Source.Camera, withPlaceholder: true },
            { source: Track.Source.ScreenShare, withPlaceholder: false },
        ],
        { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false }
    );

    const focusTrack = usePinnedTracks(layoutContext)[0];
    const speakerTrack = useSpeakerTrack(tracks);
    const { dispatch: dispatchPin } = layoutContext.pin;

    useEffect(() => {
        const screenShares = tracks.filter((track) => isScreenShareTrack(track));
        const { order, started, ended } = orderScreenShares(
            screenShareOrderRef.current,
            screenShares.map((track) => ({ sid: track.publication.trackSid, subscribed: track.publication.isSubscribed }))
        );
        screenShareOrderRef.current = order;
        const latestSid = order.at(-1);
        const latestScreenShare = screenShares.find((track) => track.publication.trackSid === latestSid);
        const switchedToSpeaker = previousLayoutRef.current !== 'speaker' && layout === 'speaker';
        previousLayoutRef.current = layout;
        const focusedScreenShareEnded =
            focusTrack?.source === Track.Source.ScreenShare && !screenShares.some((track) => isSameTrackRef(track, focusTrack));
        const stageLostScreenShare = ended && (!focusTrack || focusedScreenShareEnded);

        if (latestScreenShare && (started || stageLostScreenShare || (switchedToSpeaker && !focusTrack))) {
            dispatchPin?.({ msg: 'set_pin', trackReference: latestScreenShare });
        } else if (focusedScreenShareEnded) {
            dispatchPin?.({ msg: 'clear_pin' });
        } else if (focusTrack && !isTrackReference(focusTrack)) {
            const updatedFocusTrack = tracks.find((track) => isSameTrackRef(track, focusTrack));
            if (updatedFocusTrack !== focusTrack && isTrackReference(updatedFocusTrack)) {
                dispatchPin?.({ msg: 'set_pin', trackReference: updatedFocusTrack });
            }
        }
    }, [layout, focusTrack, tracks, dispatchPin]);

    if (layout === 'tile') {
        return (
            <div className={classes.stage}>
                <StageGrid tracks={tracks} />
            </div>
        );
    }

    const stageTrack = focusTrack ?? speakerTrack;
    const filmstripTracks = tracks.filter((track) => !isSameTrackRef(track, stageTrack));
    const filmstripLabel = filmstripVisible ? t('stage.hideFilmstrip') : t('stage.showFilmstrip');
    const floatingTrack =
        !filmstripVisible && stageTrack?.source === Track.Source.ScreenShare && speakerTrack ? speakerTrack : undefined;
    let filmstripIcon = filmstripVisible ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />;
    if (stacked) filmstripIcon = filmstripVisible ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />;

    return (
        <div className={classes.stage} data-stacked={stacked}>
            <ResizableSplit
                key={stacked ? 'stacked' : 'side'}
                className={classes.speakerSplit}
                mainClassName={classes.speakerPane}
                mainMin="60%"
                orientation={stacked ? 'vertical' : 'horizontal'}
                sideClassName={classes.filmstrip}
                sideDefault={stacked ? '--spacing-36' : '--spacing-56'}
                sideMin={stacked ? '--spacing-24' : '--spacing-32'}
                storageKey={stacked ? 'meet.filmstripHeight' : 'meet.filmstripSize'}
                main={
                    <>
                        <div className={classes.speakerTile}>
                            {stageTrack && <ParticipantTile trackRef={stageTrack} />}
                            {floatingTrack && <FloatingTile trackRef={floatingTrack} />}
                        </div>
                        {filmstripTracks.length > 0 && (
                            <Tooltip label={filmstripLabel} position={stacked ? 'top' : 'left'}>
                                <ActionIcon
                                    aria-expanded={filmstripVisible}
                                    aria-label={filmstripLabel}
                                    radius="var(--radius-rounded-full)"
                                    variant="default"
                                    onClick={() => {
                                        setFilmstripVisible((visible) => !visible);
                                    }}
                                >
                                    {filmstripIcon}
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </>
                }
                side={
                    filmstripVisible &&
                    filmstripTracks.length > 0 && (
                        <CarouselLayout orientation={stacked ? 'horizontal' : 'vertical'} tracks={filmstripTracks}>
                            <ParticipantTile />
                        </CarouselLayout>
                    )
                }
            />
        </div>
    );
}
